import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  BASE_URL,
  getToken,
  getCurrentLanguage,
} from "../../shared/utils.js/utils";
import { makeOfflineRequest } from "../../shared/utils.js/offlineRequestManager";

// Get Banks
export const getBanks = createAsyncThunk(
  "banks/getBanks",
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page);
      if (params.per_page) queryParams.append("per_page", params.per_page);
      if (params.search) queryParams.append("search", params.search);
      if (
        params.is_active !== undefined &&
        params.is_active !== null &&
        params.is_active !== ""
      ) {
        queryParams.append("is_active", params.is_active);
      }
      const url = `${BASE_URL}/banks${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to get banks!";
      return rejectWithValue(message);
    }
  },
);

// Get Bank Details
export const getBankDetails = createAsyncThunk(
  "banks/getBankDetails",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.get(`${BASE_URL}/banks/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get bank details!";
      return rejectWithValue(message);
    }
  },
);

// Add Bank
export const addBank = createAsyncThunk(
  "banks/addBank",
  async (data, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.post(`${BASE_URL}/banks`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      let message = error.response?.data?.message || "Failed to add bank!";
      if (error.response?.data?.errors) {
        message = JSON.stringify(error.response.data.errors);
      }
      return rejectWithValue(message);
    }
  },
);

// Update Bank

export const updateBank = createAsyncThunk(
  "banks/updateBank",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      // Use makeOfflineRequest for offline PATCH/PUT support
      const response = await makeOfflineRequest(`/banks/${id}`, "PUT", data);
      return response;
    } catch (error) {
      // If error is from offline queue, treat as optimistic update
      if (error.message === "OFFLINE_REQUEST_SAVED" || error.isOffline) {
        return {
          success: true,
          message: "Saved offline, will sync when online.",
        };
      }
      let message =
        error.response?.message || error.message || "Failed to update bank!";
      if (error.response?.errors) {
        message = JSON.stringify(error.response.errors);
      }
      return rejectWithValue(message);
    }
  },
);

// Toggle Bank Status
export const toggleBankStatus = createAsyncThunk(
  "banks/toggleBankStatus",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.patch(
        `${BASE_URL}/banks/${id}/toggle-status`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to toggle bank status!";
      return rejectWithValue(message);
    }
  },
);

// Delete Bank
export const deleteBank = createAsyncThunk(
  "banks/deleteBank",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.delete(`${BASE_URL}/banks/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to delete bank!";
      return rejectWithValue(message);
    }
  },
);
